import React from 'react';
import styled from 'styled-components';
import { useScreenQuery } from 'playup-web-core';
import { useRouteMatch } from 'react-router-dom';

import PlayupAppIcon from '../assets/playup-app-icon.png';
import DraftstarsAppIcon from '../assets/draftstars-app-icon.png';

interface TutorialContextType {
	showTutorial: boolean;
	openTutorialModal: () => void;
	closeTutorialModal: () => void;
}

const TutorialContext = React.createContext({} as TutorialContextType);

type ToolTipProps = {
	x: number;
	y: number;
	displayText: string;
	position: 'top' | 'right' | 'bottom' | 'left';
	staticLeft?: number;
	staticTop?: number;
};

const useTutorialContextProvider = () => {
	const context = React.useContext(TutorialContext);
	return context;
};

const TutorialContextProvider: React.FC = ({ children }) => {
	const fantasySelected = !!useRouteMatch('/fantasy');
	const [showTutorial, setShowTutorial] = React.useState<boolean>(false);

	const openTutorialModal = React.useCallback(() => {
		setShowTutorial(true);
	}, []);

	const closeTutorialModal = React.useCallback(() => {
		setShowTutorial(false);
	}, []);

	const [tooltips, setTooltips] = React.useState<ToolTipProps[]>([]);
	const { isSmall } = useScreenQuery();

	React.useEffect(() => {
		if (showTutorial) {
			document.body.style.overflow = 'hidden';
			const appSelector = document.getElementById('app-selector');
			const rewardsButton = document.getElementById('my-rewards');

			const rewardsButtonRect = rewardsButton?.getBoundingClientRect();

			const defaultTooltips: Array<ToolTipProps> = [];

			if (rewardsButtonRect) {
				defaultTooltips.push({
					x: rewardsButtonRect.left + rewardsButtonRect.width / 2,
					y: rewardsButtonRect.bottom,
					displayText: 'Check available rewards from here',
					position: 'bottom',
				});
			}

			const appSelectorRect = appSelector?.getBoundingClientRect();

			if (!isSmall && appSelectorRect) {
				defaultTooltips.push({
					x: appSelectorRect.left + appSelectorRect.width / 2,
					y: appSelectorRect.bottom,
					displayText: 'Switch between Betting and Fantasy',
					position: 'bottom',
				});
			}
			setTooltips(defaultTooltips);
		} else {
			document.body.style.overflow = 'auto';
		}
	}, [fantasySelected, isSmall, showTutorial]);

	return (
		<>
			<TutorialContext.Provider
				value={{
					showTutorial,
					openTutorialModal,
					closeTutorialModal,
				}}
			>
				{children}
			</TutorialContext.Provider>
			{showTutorial && (
				<Overlay onClick={closeTutorialModal}>
					{isSmall && (
						<BrandSelectionContainer>
							<div className=" _flex _flex-col">
								<AppIcon src={PlayupAppIcon} />
								<AppIconText>PlayUp</AppIconText>
							</div>
							<div className=" _flex _flex-col">
								<AppIcon src={DraftstarsAppIcon} />
								<AppIconText>Draftstars</AppIconText>
							</div>
						</BrandSelectionContainer>
					)}
					{isSmall && (
						<ToolTip
							position={'bottom'}
							y={150}
							x={200}
							staticLeft={50}
							staticTop={180}
							displayText={'Switch between Betting and Fantasy'}
						/>
					)}

					{tooltips.map((tooltip, index) => {
						return (
							<ToolTip
								key={index}
								position={tooltip.position}
								y={tooltip.y}
								x={tooltip.x}
								displayText={tooltip.displayText}
							/>
						);
					})}
				</Overlay>
			)}
		</>
	);
};

const ToolTip = ({ y, x, displayText, position, staticLeft, staticTop }: ToolTipProps) => {
	const [clientWidth, setClientWidth] = React.useState<number>(0);
	const [clientHeight, setClientHeight] = React.useState<number>(0);
	const containerRef = React.useRef<HTMLDivElement>(null);
	const { isSmall } = useScreenQuery();

	React.useLayoutEffect(() => {
		if (!containerRef.current?.clientHeight || !containerRef.current.clientWidth) return;

		setClientHeight(containerRef.current.clientHeight);
		setClientWidth(containerRef.current.clientWidth);

		// on first render width and height are not correct most of the times
		const t = setTimeout(() => {
			if (containerRef.current) {
				setClientHeight(containerRef.current.clientHeight);
				setClientWidth(containerRef.current.clientWidth);
			}
		});

		return () => {
			clearTimeout(t);
		};
	}, []);

	const top = React.useMemo(() => {
		if (staticTop) {
			return staticTop;
		}
		if (isSmall) {
			if (position === 'top') {
				return y - clientHeight - 15;
			}
		}

		if (position === 'bottom') {
			return y + 10;
		}

		if (position === 'right') {
			return y;
		} else {
			return y - 15;
		}
	}, [clientHeight, isSmall, position, staticTop, y]);

	const left = React.useMemo(() => {
		if (staticLeft) {
			return staticLeft;
		}

		if (position === 'bottom') {
			return x - clientWidth / 2;
		}

		if (position === 'left') {
			return x - clientWidth - 20;
		} else if (position === 'right') {
			return x;
		} else {
			return x - clientWidth / 2;
		}
	}, [clientWidth, position, staticLeft, x]);

	return (
		<ToolTipContainer
			ref={containerRef}
			style={{
				top: top,
				left: left,
				opacity: clientWidth === 0 ? 0 : 1,
			}}
		>
			{position === 'left' && <RightArrow />}
			{position === 'right' && <LeftArrow />}
			{position === 'bottom' && <TopArrow />}
			{position === 'top' && <BottomArrow />}

			<ToolTipLabel isSmall={isSmall}>{displayText}</ToolTipLabel>
		</ToolTipContainer>
	);
};

const AppIcon = styled.img`
	width: 60px;
	height: 60px;
	margin-bottom: 5px;
`;

const AppIconText = styled.p`
	font-size: 11px;
`;

const BrandSelectionContainer = styled.div`
	display: flex;
	justify-content: space-around;
	align-items: center;
	position: absolute;
	left: 5px;
	top: 50px;
	height: 120px;
	width: 300px;
	border-radius: 10px;
	color: ${({ theme }) => theme.colors.white};
	background-color: #3b3b3b;
`;
const BottomArrow = styled.div({
	position: 'absolute',
	bottom: -9,
	left: '50%',
	width: 0,
	height: 0,
	borderLeft: '10px solid transparent',
	borderRight: '10px solid transparent',
	borderTop: '10px solid white',
	transform: 'translateX(-50%)',
});

const TopArrow = styled.div({
	position: 'absolute',
	top: -9,
	left: '50%',
	width: 0,
	height: 0,
	borderLeft: '10px solid transparent',
	borderRight: '10px solid transparent',
	borderBottom: '10px solid white',
	transform: 'translateX(-50%)',
});
const LeftArrow = styled.div({
	position: 'absolute',
	width: 0,
	height: 0,
	borderWidth: 10,
	borderStyle: 'solid',
	borderColor: 'transparent #FFFFFF transparent transparent',
	top: 11,
	left: -19,
});
const RightArrow = styled.div({
	position: 'absolute',
	top: '50%',
	right: -9,
	width: 0,
	height: 0,
	borderLeft: '10px solid white',
	borderTop: '10px solid transparent',
	borderBottom: '10px solid transparent',
	transform: 'translateY(-50%)',
});
const ToolTipLabel = styled.span<{ isSmall: boolean }>(({ isSmall }) => ({
	color: '#1A1A1A',
	fontSize: isSmall ? 11 : 16,
	fontWeight: 500,
}));
const Overlay = styled.div({
	position: 'fixed',
	top: 0,
	left: 0,
	zIndex: 9999,
	width: '100%',
	height: '100%',
	backgroundColor: 'rgba(0,0,0,0.5)',
});

const TutorialWrapper = ({ children, displayText }: { children: React.ReactNode; displayText: string }) => {
	return (
		<>
			<div className="tooltip">
				{children}
				<span className="tooltiptext">{displayText}</span>
			</div>
		</>
	);
};

const ToolTipContainer = styled.div({
	position: 'absolute',
	backgroundColor: 'white',
	paddingLeft: 20,
	paddingRight: 20,
	paddingTop: 14,
	paddingBottom: 14,
	borderRadius: 6,
});

export { TutorialContextProvider, TutorialContext, TutorialWrapper, useTutorialContextProvider };
